import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '@/contexts/DataContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';  // ← ADD THIS
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { InfoIcon, ArrowLeft, ArrowRight, Maximize2, Minimize2 } from 'lucide-react';

export default function PitchCreateTraction() {
  const { saveTempPitch, getTempPitch } = useData();
  const navigate = useNavigate();
  const [fullscreen, setFullscreen] = useState(false);
  const [form, setForm] = useState(() => {
    const saved = getTempPitch();
    return {
      traction: saved?.traction || {
        customerInterviews: 0,
        waitlistSignups: 0,
        lettersOfIntent: 0,
        pilotCustomers: 0,
        mvpStatus: 'none',
        mvpUrl: '',
        monthlyActiveUsers: 0,
        revenueRunRate: 0,
      }
    };
  });

  useEffect(() => {
    saveTempPitch(form);
  }, [form, saveTempPitch]);

  const handleBack = () => {
    navigate('/pitch/new/basics');
  };

  const handleNext = () => {
    navigate('/pitch/new/competition');
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setFullscreen(false);
      }
    }
  };

  const updateField = (field: string, value: any) => {
    setForm(prev => ({
      ...prev,
      traction: {
        ...prev.traction,
        [field]: value
      }
    }));
  };

  return (
    <div className={`${fullscreen ? 'fixed inset-0 z-50 bg-background overflow-auto p-8' : ''}`}>
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold">Traction & Validation</h1>
            <p className="text-muted-foreground mt-1">Step 2 of 8 · Show us what you've already accomplished</p>
          </div>
          <Button variant="outline" size="icon" onClick={toggleFullscreen}>
            {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Customer Validation</CardTitle>
            <CardDescription>
              The strongest signal for pre-seed startups is real customer interest.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-8">
            <Alert className="bg-green-50 border-green-200">
              <InfoIcon className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-700">
                Any traction you have dramatically increases your chances of funding. Be honest if you have none yet.
              </AlertDescription>
            </Alert>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-base font-medium">Customer Interviews</Label>
                <Input
                  type="number"
                  value={form.traction.customerInterviews}
                  onChange={e => updateField('customerInterviews', parseInt(e.target.value) || 0)}
                  min="0"
                  className="text-base py-6"
                />
                <p className="text-sm text-muted-foreground">Number of customer discovery interviews conducted</p>
              </div>

              <div className="space-y-2">
                <Label className="text-base font-medium">Waitlist Signups</Label>
                <Input
                  type="number"
                  value={form.traction.waitlistSignups}
                  onChange={e => updateField('waitlistSignups', parseInt(e.target.value) || 0)}
                  min="0"
                  className="text-base py-6"
                />
                <p className="text-sm text-muted-foreground">People who've signed up to use your product</p>
              </div>

              <div className="space-y-2">
                <Label className="text-base font-medium">Letters of Intent (LOIs)</Label>
                <Input
                  type="number"
                  value={form.traction.lettersOfIntent}
                  onChange={e => updateField('lettersOfIntent', parseInt(e.target.value) || 0)}
                  min="0"
                  className="text-base py-6"
                />
                <p className="text-sm text-muted-foreground">Non-binding agreements from potential customers</p>
              </div>

              <div className="space-y-2">
                <Label className="text-base font-medium">Pilot Customers</Label>
                <Input
                  type="number"
                  value={form.traction.pilotCustomers}
                  onChange={e => updateField('pilotCustomers', parseInt(e.target.value) || 0)}
                  min="0"
                  className="text-base py-6"
                />
                <p className="text-sm text-muted-foreground">Customers currently testing/paying for your solution</p>
              </div>
            </div>

            <div className="space-y-4">
              <Label className="text-base font-medium">MVP Status</Label>
              <Select 
                value={form.traction.mvpStatus}
                onValueChange={(value) => updateField('mvpStatus', value)}
              >
                <SelectTrigger className="text-base py-6">
                  <SelectValue placeholder="Select MVP status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No MVP yet (Idea stage)</SelectItem>
                  <SelectItem value="in_development">In Development</SelectItem>
                  <SelectItem value="beta">Beta Testing</SelectItem>
                  <SelectItem value="live">Live / Launched</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.traction.mvpStatus !== 'none' && (
              <div className="space-y-2">
                <Label className="text-base font-medium">MVP URL</Label>
                <Input
                  value={form.traction.mvpUrl}
                  onChange={e => updateField('mvpUrl', e.target.value)}
                  placeholder="https://..."
                  className="text-base py-6"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-base font-medium">Monthly Active Users</Label>
                <Input
                  type="number"
                  value={form.traction.monthlyActiveUsers}
                  onChange={e => updateField('monthlyActiveUsers', parseInt(e.target.value) || 0)}
                  min="0"
                  className="text-base py-6"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-base font-medium">Monthly Revenue Run Rate ($)</Label>
                <Input
                  type="number"
                  value={form.traction.revenueRunRate}
                  onChange={e => updateField('revenueRunRate', parseInt(e.target.value) || 0)}
                  min="0"
                  className="text-base py-6"
                />
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex justify-between pt-6">
            <Button onClick={handleBack} variant="outline" size="lg" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back: Basics
            </Button>
            <Button onClick={handleNext} size="lg" className="gap-2">
              Next: Competition
              <ArrowRight className="h-4 w-4" />
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}